import { Box } from '@mui/material';

import type { SupportedLocale } from '@/shared/i18n';

/**
 * Small flag glyphs shown next to each option in the {@link LanguageSwitcher} menu.
 *
 * These are simplified, hand-drawn SVGs — enough visual detail to be instantly recognisable
 * at ~20px — rather than a third-party flag-icon package (`flag-icons`, `react-country-flag`,
 * …). Reasons: only 8 flags are needed here, none of them are added/removed often, and this
 * keeps the app self-contained (no extra dependency, no CDN fetch, no font-glyph rendering
 * quirks across OSes the way emoji flags have on some platforms).
 *
 * The fill colours below are each country's official flag colours — that's inherent content,
 * not a design-system choice, so (like the native language names in
 * `features/store-settings/ui/options.ts`) they're deliberately literal rather than theme
 * tokens. Only the surrounding frame (border/corner radius) uses theme tokens.
 *
 * Locale → country mapping: every non-English locale here maps unambiguously to one country
 * (ru→Russia, zh-CN→China, zh-TW→Taiwan, de→Germany, tr→Turkey, hi→India, ja→Japan). 'en' has
 * no single universal "English" flag; this uses the Union Jack (GB), the common convention for
 * a generic English option.
 */

const UNIT_STAR_R09 =
  '0,-0.9 0.202,-0.278 0.856,-0.278 0.327,0.106 0.529,0.728 0,0.344 -0.529,0.728 -0.327,0.106 -0.856,-0.278 -0.202,-0.278';

function FlagGB() {
  return (
    <svg viewBox="0 0 60 30" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFFFFF" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="2" />
      <path d="M30,0 V30 M0,15 H60" stroke="#FFFFFF" strokeWidth="10" />
      <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );
}

function FlagRU() {
  return (
    <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="30" height="6.667" fill="#FFFFFF" />
      <rect y="6.667" width="30" height="6.667" fill="#0039A6" />
      <rect y="13.333" width="30" height="6.667" fill="#D52B1E" />
    </svg>
  );
}

function FlagDE() {
  return (
    <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="30" height="6.667" fill="#000000" />
      <rect y="6.667" width="30" height="6.667" fill="#DD0000" />
      <rect y="13.333" width="30" height="6.667" fill="#FFCE00" />
    </svg>
  );
}

function FlagTR() {
  return (
    <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="30" height="20" fill="#E30A17" />
      <circle cx="11" cy="10" r="5.5" fill="#FFFFFF" />
      <circle cx="12.6" cy="10" r="4.6" fill="#E30A17" />
      <polygon
        points="19,8.6 19.314,9.567 20.332,9.567 19.469,10.165 19.823,11.133 19,10.535 18.177,11.133 18.531,10.165 17.669,9.567 18.686,9.567"
        fill="#FFFFFF"
      />
    </svg>
  );
}

function FlagIN() {
  return (
    <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="30" height="6.667" fill="#FF9933" />
      <rect y="6.667" width="30" height="6.667" fill="#FFFFFF" />
      <rect y="13.333" width="30" height="6.667" fill="#138808" />
      <g stroke="#000080" fill="none" strokeWidth="0.25">
        <circle cx="15" cy="10" r="2.6" strokeWidth="0.35" />
        <line x1="15" y1="10" x2="17.6" y2="10" />
        <line x1="15" y1="10" x2="16.838" y2="11.838" />
        <line x1="15" y1="10" x2="15" y2="12.6" />
        <line x1="15" y1="10" x2="13.162" y2="11.838" />
        <line x1="15" y1="10" x2="12.4" y2="10" />
        <line x1="15" y1="10" x2="13.162" y2="8.162" />
        <line x1="15" y1="10" x2="15" y2="7.4" />
        <line x1="15" y1="10" x2="16.838" y2="8.162" />
      </g>
      <circle cx="15" cy="10" r="0.4" fill="#000080" />
    </svg>
  );
}

function FlagJP() {
  return (
    <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="30" height="20" fill="#FFFFFF" />
      <circle cx="15" cy="10" r="6" fill="#BC002D" />
    </svg>
  );
}

function FlagCN() {
  return (
    <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="30" height="20" fill="#DE2910" />
      <polygon
        points="6,2.6 6.539,4.258 8.283,4.258 6.872,5.283 7.411,6.942 6,5.917 4.589,6.942 5.128,5.283 3.717,4.258 5.461,4.258"
        fill="#FFDE00"
      />
      <polygon points={UNIT_STAR_R09} fill="#FFDE00" transform="translate(10.8,2.4) scale(0.55)" />
      <polygon points={UNIT_STAR_R09} fill="#FFDE00" transform="translate(12.6,5) scale(0.55) rotate(23)" />
      <polygon points={UNIT_STAR_R09} fill="#FFDE00" transform="translate(11.6,8) scale(0.55) rotate(45)" />
      <polygon points={UNIT_STAR_R09} fill="#FFDE00" transform="translate(9,9.3) scale(0.55) rotate(70)" />
    </svg>
  );
}

function FlagTW() {
  return (
    <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="30" height="20" fill="#FE0000" />
      <rect width="15" height="10" fill="#000095" />
      <g stroke="#FFFFFF" strokeWidth="0.6">
        <line x1="7.5" y1="5" x2="11.8" y2="5" />
        <line x1="7.5" y1="5" x2="11.224" y2="7.15" />
        <line x1="7.5" y1="5" x2="9.65" y2="8.724" />
        <line x1="7.5" y1="5" x2="7.5" y2="9.3" />
        <line x1="7.5" y1="5" x2="5.35" y2="8.724" />
        <line x1="7.5" y1="5" x2="3.776" y2="7.15" />
        <line x1="7.5" y1="5" x2="3.2" y2="5" />
        <line x1="7.5" y1="5" x2="3.776" y2="2.85" />
        <line x1="7.5" y1="5" x2="5.35" y2="1.276" />
        <line x1="7.5" y1="5" x2="7.5" y2="0.7" />
        <line x1="7.5" y1="5" x2="9.65" y2="1.276" />
        <line x1="7.5" y1="5" x2="11.224" y2="2.85" />
      </g>
      <circle cx="7.5" cy="5" r="3" fill="#FFFFFF" />
    </svg>
  );
}

const FLAG_COMPONENTS: Record<SupportedLocale, () => React.JSX.Element> = {
  en: FlagGB,
  ru: FlagRU,
  'zh-CN': FlagCN,
  'zh-TW': FlagTW,
  de: FlagDE,
  tr: FlagTR,
  hi: FlagIN,
  ja: FlagJP,
};

export interface CountryFlagProps {
  /** Which locale's flag to render. */
  locale: SupportedLocale;
  /** Rendered width in px; height follows at a 10:7 ratio. Defaults to 20. */
  size?: number;
}

/**
 * Renders the flag associated with a supported locale in a small, rounded, bordered tile.
 * Purely decorative (the language name text next to it already conveys the meaning), so the
 * SVG is `aria-hidden` and the component exposes no accessible name of its own.
 */
export function CountryFlag({ locale, size = 20 }: CountryFlagProps) {
  const Flag = FLAG_COMPONENTS[locale];
  return (
    <Box
      sx={{
        width: size,
        height: (size * 7) / 10,
        borderRadius: '3px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        display: 'inline-flex',
        lineHeight: 0,
      }}
    >
      <Flag />
    </Box>
  );
}
